import { useEffect, useReducer, useState } from 'react';
import { GroupBase } from 'react-select';
import { Entity } from 'utils/typeguards/cfEntities';
import {
  allowedSections,
  AllowedSectionsType,
  DataEntity,
  generateOptionValueFromEntity,
  getTypeFromDataEntity,
  ResourceType,
} from 'components/PlatformResourceSelector/utils';

export type SourceDataType = Record<AllowedSectionsType, DataEntity[]>;
export type MappedDataType = Partial<SourceDataType>;
export type IndexedDataType = Record<string, DataEntity>;

export interface OptionValue {
  label: string;
  tooltip?: string;
  type: ResourceType;
  value: string;
  icon?: React.ReactNode;
  featured?: boolean;
  entity?: DataEntity;
}

export interface GroupValue extends GroupBase<OptionValue> {
  expand: boolean;
  label: string;
  options: OptionValue[];
  type: ResourceType;
}

type OptionsReducerAction = {
  type: 'expand' | 'update' | 'clear';
  data?: GroupValue[];
  groupLabel?: string;
};

const useOptionsReducer = (state: GroupValue[], actions: OptionsReducerAction): GroupValue[] => {
  switch (actions.type) {
    case 'update':
      return [...(actions.data ? actions.data : [])];
    case 'expand': {
      const group = state.find((groupValue) => groupValue.label === actions.groupLabel);
      if (group) {
        group.expand = true;
      }
      return [...state];
    }
    case 'clear':
      return [];
    default:
      return state;
  }
};

export const fromDataToOptionValue = (entity: DataEntity & { iconComponent?: React.ReactNode; featured?: boolean }): OptionValue => {
  return {
    label: entity.id,
    type: getTypeFromDataEntity(entity),
    value: generateOptionValueFromEntity(entity),
    icon: entity.iconComponent,
    featured: entity.featured,
    ...(entity.description && { tooltip: entity.description }),
    entity,
  };
};

export type SectionSerializerType = (resources: PlatformResourceSelectorDataType) => GroupValue[];

type SerializeOptionsType = {
  type: ResourceType;
  resources: PlatformResourceSelectorDataType;
  isSearching?: boolean;
  sectionSerializer?: SectionSerializerType;
};

const serializeOptions = ({ type, resources, isSearching = false, sectionSerializer }: SerializeOptionsType): GroupValue[] => {
  if (isSearching) {
    return [
      {
        expand: false,
        label: 'Search Results',
        options: resources.total.map((value) => {
          return fromDataToOptionValue(value);
        }),
        type,
      },
    ];
  }

  if (sectionSerializer) return sectionSerializer(resources);

  return Object.entries(resources.mapped).map(([key, values]) => {
    return {
      expand: false,
      label: allowedSections[key as AllowedSectionsType].label(type),
      options: values.map((value) => {
        return fromDataToOptionValue(value);
      }),
      type,
    };
  });
};

export type PlatformResourceSelectorDataType = {
  indexed: IndexedDataType;
  mapped: MappedDataType;
  total: DataEntity[];
};

export type PlatformResourceSelectorDataStateType = {
  addFromExternal?: (entity: Entity) => void;
  data: PlatformResourceSelectorDataType;
  isLoading: boolean;
  isSearching?: boolean;
  sectionSerializer?: SectionSerializerType;
  type: ResourceType;
};

export const usePlatformResourceSelector = (
  { data, isLoading, isSearching, sectionSerializer, type }: PlatformResourceSelectorDataStateType,
  selectedId?: string,
) => {
  const [value, updateValue] = useState<OptionValue | undefined>();
  const [options, setOptions] = useReducer(useOptionsReducer, []);
  const expandGroup = (groupName?: string) => {
    if (groupName) {
      setOptions({ type: 'expand', groupLabel: groupName });
    }
  };

  useEffect(() => {
    if (!isLoading && data.total.length > 0) {
      const serialized = serializeOptions({ type, resources: data, isSearching, sectionSerializer });
      setOptions({ type: 'update', data: serialized });
    } else if (isLoading && Object.keys(data).length) {
      setOptions({ type: 'clear' });
    }
  }, [data.total.length, isLoading, isSearching]);

  useEffect(() => {
    if (selectedId && selectedId !== value?.value) {
      const currentValue = data.indexed[selectedId];
      if (currentValue) {
        updateValue(fromDataToOptionValue(currentValue));
      }
    } else if (!selectedId && value?.value) {
      updateValue(undefined);
    }
  }, [data.indexed, selectedId]);

  return {
    data,
    expandGroup,
    isLoading,
    options,
    type,
    value,
  };
};
